import { ref, computed, getCurrentInstance } from 'vue';
import { useStore, useRouter } from '@/utils/vue-api';
import { useStoreCore } from '@/hooks';
import { useNavigation } from '@rebuild/hooks/useNavigation';
import { CACHE_NAVIGATOR_HISTORY } from '@/const/host';
import { translate } from '@/locales';

export function useHistoryNav() {
	const vm = getCurrentInstance();
	const store = useStore();
	const router = useRouter();

	const { shouldShowUI } = useStoreCore();
	const { buildTemplatePath } = useNavigation();

	const navigationHistory = ref([]);
	const getNavigationList = computed(() => navigationHistory.value);

	const checkHomeView = route => {
		return route.name.includes('home');
	};

	const isCurrentPage = record => {
		const latestRoute = vm.proxy.$route;
		const currentName = getRouteTitle(latestRoute);
		return record.name === currentName;
	};

	const getRouteTitle = route => {
		const { menuId, moduleId, subPath } = route.params || {};

		// 详情页标题
		if (subPath === 'detail') {
			const map = {
				'news': translate('enterprise.news-detail'),
				'report': translate('enterprise.report-detail')
			};
			return map[moduleId] || '';
		}

		let mid;
		// 历史自定义模块访问 "/Custom"
		if (route.meta.source === 'module' && route.params.moduleId === 'custom') {
			mid = route.query.moduleId;
		} else {
			mid = moduleId;
		}

		if (mid) {
			const allModules = store.getters.getPageAllModules || [];
			const module = allModules.find(m => m.moduleId === mid);
			if (module?.name) return module.name;
		}

		if (menuId) {
			const { pageMenuVOS = [] } = store.getters.getCommonData;
			const menu = pageMenuVOS.find(m => m.id === menuId);

			if (menu?.moduleVOS?.length) {
				// 无组件ID场景，默认取第一个模块做展示，当前导航栏仅展示模块名称
				const [fstModule] = menu.moduleVOS;
				return fstModule.name;
			}
		}

		return '';
	};
	const getHomeTitle = () => translate('enterprise.home');

	/**
	 * 初始化导航历史
	 * @param {Object} currentRoute - 当前路由
	 */
	const initNavigation = currentRoute => {
		// 每次执行时都要覆盖导航缓存
		let history = [];
		const currentRecord = createNavRecord(currentRoute);
		if (checkHomeView(currentRoute)) {
			// 访问首页：[首页]
			history = [currentRecord];
		} else {
			// 非首页：[首页 + patched 标识, 当前页]
			if (shouldShowUI('home')) {
				const homeRoute = router.resolve({ path: buildTemplatePath('index', 'flow') }).route;
				const homeRecord = {
					...createNavRecord(homeRoute),
					patched: true
				};
				history = [homeRecord, currentRecord];
			} else {
				history = [currentRecord];
			}
		}

		navigationHistory.value = history;
		saveToCache(history);
	};

	/**
	 * 推进或截断导航历史到指定记录
	 * @param {Array} history - 当前历史
	 * @param {Object} record - 当前路由记录
	 * @returns {Array} 新的历史
	 */
	const syncHistoryToRecord = (history, record) => {
		const existingIndex = history.findIndex(item => item.name === record.name);
		if (existingIndex === -1) {
			// 新页面：添加到末尾
			return [...history, record];
		} else {
			// 已存在：截断到该位置
			return history.slice(0, existingIndex + 1);
		}
	};
	/**
	 * 更新导航历史
	 * @param {Object} toRoute - 目标路由
	 */
	const updateNavigation = toRoute => {
		const newRecord = createNavRecord(toRoute);
		let history = [...navigationHistory.value];
		if (checkHomeView(toRoute)) {
			// 访问首页：重置
			history = [newRecord];
		} else {
			// 推进/截断到目标页
			history = syncHistoryToRecord(history, newRecord);
		}

		navigationHistory.value = history;
		saveToCache(history);
	};

	/**
	 * 创建导航记录
	 * @param {Object} route - 路由对象
	 * @returns {Object} 导航记录
	 */
	const createNavRecord = route => {
		const isHomeView = checkHomeView(route);
		const record = {
			isHome: isHomeView,
			name: isHomeView ? getHomeTitle() : getRouteTitle(route),
			path: route.path,
			fullPath: route.fullPath,
			timestamp: Date.now()
		};
		return record;
	};

	const saveToCache = history => {
		sessionStorage.setItem(CACHE_NAVIGATOR_HISTORY, JSON.stringify(history));
	};

	return {
		getNavigationList,
		initNavigation,
		updateNavigation,
		isCurrentPage
	};
}
